import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Text,
  Button,
  Checkbox,
  Input,
  Tooltip,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  DialogTrigger,
  DataGrid,
  DataGridBody,
  DataGridCell,
  DataGridHeader,
  DataGridHeaderCell,
  DataGridRow,
  TableCellLayout,
  Skeleton,
  SkeletonItem,
  MessageBar,
  MessageBarBody,
  MessageBarActions,
  Spinner,
  mergeClasses,
  createTableColumn,
  type TableColumnDefinition,
  type TableColumnSizingOptions,
} from '@fluentui/react-components';
import {
  ArrowDownloadRegular,
  AddRegular,
  EditRegular,
  CheckmarkRegular,
  DismissRegular,
  DeleteRegular,
  DiamondFilled,
  InfoFilled,
  ArrowUndoRegular,
} from '@fluentui/react-icons';
import { useProgramChangesPageStyles } from '../../styles/ProgramChangesPage.styles';
import { exportToCsv } from '../../utils/exportToCsv';
import { compareString } from '../../utils/comparators';
import { TIER_KEYS, TIER_LABELS, type TierKey } from '../../constants/tiers';
import { useBenefitsPerTier } from '../../contexts/BenefitsPerTierContext';
import {
  EMPTY_ELIGIBILITY,
  type Benefit,
} from '../../services/benefitsPerTierService';
import {
  getFriendlyApiErrorMessage,
  logApiError,
} from '../../services/apiErrorMessages';
import { formatPendingTooltip as formatSharedPendingTooltip } from './formatPendingTooltip';
import { ProgramChangesLastUpdatedHeader } from './ProgramChangesLastUpdatedHeader';

interface BenefitsPerTierTabProps {
  isLocked: boolean;
}

type ColumnId = 'name' | TierKey | 'actions';

interface Tier {
  key: TierKey;
  label: string;
  diamondClass: string;
}

const PENDING_ID = '__pending_new_benefit__';

const columnWeights: Record<ColumnId, number> = {
  name: 3,
  silver: 1.4,
  gold: 1.4,
  platinum: 1.4,
  actions: 1,
};

const columnMinWidths: Record<ColumnId, number> = {
  name: 200,
  silver: 110,
  gold: 110,
  platinum: 110,
  actions: 110,
};

const formatPendingTooltip = (benefit: Benefit): string =>
  benefit.pending
    ? formatSharedPendingTooltip(benefit.pending, {
        currentName: benefit.name,
        updateSummary: 'eligibility updated',
      })
    : '';

export const BenefitsPerTierTab: React.FC<BenefitsPerTierTabProps> = ({ isLocked }) => {
  const styles = useProgramChangesPageStyles();
  const {
    benefits,
    loading,
    loadError,
    refresh,
    addBenefit,
    updateBenefit,
    removeBenefit,
    cancelPendingForBenefit,
  } = useBenefitsPerTier();

  const tiers: Tier[] = useMemo(
    () =>
      TIER_KEYS.map((key) => ({
        key,
        label: TIER_LABELS[key],
        diamondClass: styles[`tierDiamond${TIER_LABELS[key]}` as keyof typeof styles],
      })),
    [styles],
  );

  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftEligibility, setDraftEligibility] =
    useState<Record<TierKey, boolean> | null>(null);
  const [draftName, setDraftName] = useState<string>('');
  const [pendingNewRow, setPendingNewRow] = useState<Benefit | null>(null);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [inlineError, setInlineError] = useState<string | null>(null);

  const gridContainerRef = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState<number>(0);

  useEffect(() => {
    const el = gridContainerRef.current;
    if (!el) return;
    const update = () => setContainerWidth(el.clientWidth);
    update();
    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(update);
      observer.observe(el);
      return () => observer.disconnect();
    }
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const columnSizingOptions = useMemo<TableColumnSizingOptions>(() => {
    const activeIds: ColumnId[] = ['name', 'silver', 'gold', 'platinum'];
    if (!isLocked) activeIds.push('actions');

    const totalMin = activeIds.reduce((sum, id) => sum + columnMinWidths[id], 0);
    const totalWeight = activeIds.reduce((sum, id) => sum + columnWeights[id], 0);
    const available = Math.max(containerWidth - 2, totalMin);
    const extra = Math.max(0, available - totalMin);

    const sizing: TableColumnSizingOptions = {};
    activeIds.forEach((id) => {
      const ideal = columnMinWidths[id] + (extra * columnWeights[id]) / totalWeight;
      sizing[id] = {
        minWidth: columnMinWidths[id],
        idealWidth: Math.round(ideal),
      };
    });
    return sizing;
  }, [containerWidth, isLocked]);

  const displayBenefits = useMemo(() => {
    return pendingNewRow ? [pendingNewRow, ...benefits] : benefits;
  }, [pendingNewRow, benefits]);

  const isMutating = savingId !== null || deletingId !== null;

  const startEdit = (benefit: Benefit) => {
    if (isMutating || isLocked) return;
    setEditingId(benefit.id);
    setDraftEligibility({ ...benefit.eligibility });
    setDraftName(benefit.name);
    setInlineError(null);
  };

  const cancelEdit = () => {
    if (editingId === PENDING_ID) {
      setPendingNewRow(null);
    }
    setEditingId(null);
    setDraftEligibility(null);
    setDraftName('');
    setInlineError(null);
  };

  const saveEdit = async () => {
    if (!editingId || !draftEligibility) return;
    const trimmed = draftName.trim();
    if (!trimmed) {
      setInlineError('Benefit name is required.');
      return;
    }

    const targetKey = trimmed.toLowerCase();
    const collision = benefits.some(
      (b) => b.id !== editingId && b.name.trim().toLowerCase() === targetKey,
    );
    if (collision) {
      setInlineError(`A benefit named "${trimmed}" already exists.`);
      return;
    }

    setSavingId(editingId);
    setActionError(null);
    setInlineError(null);
    try {
      if (editingId === PENDING_ID) {
        await addBenefit({ name: trimmed, eligibility: draftEligibility });
        setPendingNewRow(null);
      } else {
        await updateBenefit(editingId, {
          name: trimmed,
          eligibility: draftEligibility,
        });
      }
      setEditingId(null);
      setDraftEligibility(null);
      setDraftName('');
    } catch (err) {
      logApiError('BenefitsPerTierTab.saveEdit', err);
      setInlineError(getFriendlyApiErrorMessage(err, 'Failed to save the benefit.'));
    } finally {
      setSavingId(null);
    }
  };

  const handleAddBenefit = () => {
    if (isLocked || isMutating) return;
    if (editingId) cancelEdit();
    const newBenefit: Benefit = {
      id: PENDING_ID,
      name: '',
      eligibility: { ...EMPTY_ELIGIBILITY },
    };
    setPendingNewRow(newBenefit);
    setEditingId(PENDING_ID);
    setDraftEligibility({ ...EMPTY_ELIGIBILITY });
    setDraftName('');
    setInlineError(null);
  };

  const confirmDelete = async () => {
    if (!pendingDeleteId) return;
    const id = pendingDeleteId;
    setDeletingId(id);
    setDeleteError(null);
    try {
      await removeBenefit(id);
      if (editingId === id) {
        setEditingId(null);
        setDraftEligibility(null);
        setDraftName('');
      }
      setPendingDeleteId(null);
    } catch (err) {
      logApiError('BenefitsPerTierTab.confirmDelete', err);
      setDeleteError(
        getFriendlyApiErrorMessage(err, 'Failed to delete the benefit.'),
      );
    } finally {
      setDeletingId(null);
    }
  };

  const handleUndoPending = async (id: string) => {
    setActionError(null);
    try {
      await cancelPendingForBenefit(id);
    } catch (err) {
      logApiError('BenefitsPerTierTab.handleUndoPending', err);
      setActionError(
        getFriendlyApiErrorMessage(err, 'Failed to discard the pending change.'),
      );
    }
  };

  const pendingBenefit = displayBenefits.find((b) => b.id === pendingDeleteId) || null;

  const columns: TableColumnDefinition<Benefit>[] = useMemo(() => {
    const cols: TableColumnDefinition<Benefit>[] = [
      createTableColumn<Benefit>({
        columnId: 'name',
        compare: (a, b) => compareString(a.name, b.name),
        renderHeaderCell: () => 'Benefits',
        renderCell: () => null,
      }),
      ...tiers.map((tier) =>
        createTableColumn<Benefit>({
          columnId: tier.key,
          compare: (a, b) =>
            (a.eligibility[tier.key] ? 1 : 0) - (b.eligibility[tier.key] ? 1 : 0),
          renderHeaderCell: () => (
            <span className={styles.tierHeader}>
              <DiamondFilled className={tier.diamondClass} />
              {tier.label}
            </span>
          ),
          renderCell: () => null,
        }),
      ),
    ];

    if (!isLocked) {
      cols.push(
        createTableColumn<Benefit>({
          columnId: 'actions',
          renderHeaderCell: () => '',
          renderCell: () => null,
        }),
      );
    }

    return cols;
  }, [tiers, isLocked, styles]);

  const renderCellContent = (columnId: ColumnId, benefit: Benefit) => {
    const isEditing = editingId === benefit.id;
    const rowSaving = savingId === benefit.id;
    const rowDeleting = deletingId === benefit.id;

    if (columnId === 'name') {
      if (isEditing) {
        return (
          <div className={styles.benefitNameContent}>
            <Input
              value={draftName}
              placeholder="Benefit name"
              onChange={(_, data) => {
                setDraftName(data.value);
                if (inlineError) setInlineError(null);
              }}
              aria-label="Benefit name"
              className={styles.benefitNameInput}
              autoFocus={benefit.id === PENDING_ID}
              disabled={rowSaving}
            />
            {inlineError && (
              <Text className={styles.inlineErrorText}>{inlineError}</Text>
            )}
          </div>
        );
      }
      const nameClass =
        benefit.pending?.operation === 'create'
          ? styles.categoryNameSwatchCreate
          : benefit.pending?.operation === 'rename'
            ? styles.categoryNameSwatchRename
            : benefit.pending?.operation === 'delete'
              ? styles.categoryNameSwatchDelete
              : undefined;
      return (
        <div className={styles.benefitNameContent}>
          {benefit.pending && (
            <Tooltip
              relationship="description"
              content={formatPendingTooltip(benefit)}
            >
              <span
                className={styles.pendingInfoIcon}
                role="img"
                aria-label="Pending change details"
                tabIndex={0}
              >
                <InfoFilled />
              </span>
            </Tooltip>
          )}
          <Text className={nameClass}>{benefit.name}</Text>
        </div>
      );
    }

    if (columnId === 'actions') {
      if (isEditing) {
        return (
          <div className={styles.editActions}>
            <Button
              appearance="primary"
              icon={rowSaving ? <Spinner size="tiny" /> : <CheckmarkRegular />}
              onClick={() => void saveEdit()}
              size="small"
              disabled={!draftName.trim() || rowSaving}
            >
              Save
            </Button>
            <Tooltip content="Cancel" relationship="label">
              <Button
                appearance="subtle"
                icon={<DismissRegular />}
                onClick={cancelEdit}
                aria-label="Cancel"
                size="small"
                disabled={rowSaving}
              />
            </Tooltip>
            <span className={styles.editActionsSpacer} aria-hidden />
            {!isLocked && benefit.id !== PENDING_ID && (
              <Tooltip content="Delete benefit" relationship="label">
                <Button
                  appearance="subtle"
                  icon={<DeleteRegular />}
                  onClick={() => {
                    setDeleteError(null);
                    setPendingDeleteId(benefit.id);
                  }}
                  aria-label={`Delete ${benefit.name || 'benefit'}`}
                  className={styles.deleteButton}
                  size="small"
                  disabled={rowSaving || rowDeleting}
                />
              </Tooltip>
            )}
          </div>
        );
      }
      if (benefit.pending?.operation === 'delete') {
        return (
          <Tooltip content="Undo delete (cancel pending)" relationship="label">
            <Button
              appearance="subtle"
              icon={<ArrowUndoRegular />}
              onClick={() => void handleUndoPending(benefit.id)}
              aria-label={`Undo delete of ${benefit.name}`}
              size="small"
              disabled={isMutating}
            />
          </Tooltip>
        );
      }
      return (
        <Tooltip content="Edit" relationship="label">
          <Button
            appearance="subtle"
            icon={<EditRegular />}
            onClick={() => startEdit(benefit)}
            aria-label={`Edit ${benefit.name}`}
            size="small"
            disabled={isMutating}
          />
        </Tooltip>
      );
    }

    const tierKey = columnId as TierKey;
    if (isEditing && draftEligibility) {
      return (
        <Checkbox
          checked={draftEligibility[tierKey]}
          onChange={(_, data) =>
            setDraftEligibility((prev) =>
              prev ? { ...prev, [tierKey]: !!data.checked } : prev,
            )
          }
          aria-label={`${benefit.name || 'New benefit'} eligible for ${tierKey}`}
          disabled={rowSaving}
        />
      );
    }
    const currentEligible = benefit.eligibility[tierKey];
    const currentLabel = currentEligible ? 'Eligible' : '-';
    const prevEligibility = benefit.pending?.previousEligibility;
    if (prevEligibility) {
      const prevEligible = !!prevEligibility[tierKey];
      if (prevEligible !== currentEligible) {
        if (currentEligible) {
          return (
            <span className={styles.tierEligibilityAdded} title="Pending: tier marked eligible">
              {currentLabel}
            </span>
          );
        }
        return (
          <span className={styles.tierEligibilityRemoved} title="Pending: tier marked not eligible">
            Eligible
          </span>
        );
      }
    }
    return currentLabel;
  };

  const showSkeleton = loading && benefits.length === 0;

  const hasPendingCreate = displayBenefits.some((b) => b.pending?.operation === 'create');
  const hasPendingRename = displayBenefits.some((b) => b.pending?.operation === 'rename');
  const hasPendingDelete = displayBenefits.some((b) => b.pending?.operation === 'delete');
  const hasEligibilityAdded = displayBenefits.some((b) => {
    const prev = b.pending?.previousEligibility;
    if (!prev) return false;
    return TIER_KEYS.some((k) => !prev[k] && b.eligibility[k]);
  });
  const hasEligibilityRemoved = displayBenefits.some((b) => {
    const prev = b.pending?.previousEligibility;
    if (!prev) return false;
    return TIER_KEYS.some((k) => prev[k] && !b.eligibility[k]);
  });
  const hasPendingChanges =
    hasPendingCreate ||
    hasPendingRename ||
    hasPendingDelete ||
    hasEligibilityAdded ||
    hasEligibilityRemoved;

  return (
    <div>
      <ProgramChangesLastUpdatedHeader selectedTab="benefits-per-tier" />
      
      <div className={styles.toolbar}>
        <Button
          appearance="subtle"
          icon={<AddRegular />}
          disabled={isLocked || isMutating || loading}
          onClick={handleAddBenefit}
        >
          Add benefit
        </Button>
        <Button
          appearance="subtle"
          icon={<ArrowDownloadRegular />}
          onClick={() =>
            exportToCsv<Benefit>({
              fileName: 'benefits-per-tier',
              columns: [
                { header: 'Benefit', accessor: (b) => b.name },
                ...tiers.map((t) => ({
                  header: t.label,
                  accessor: (b: Benefit) => (b.eligibility[t.key] ? 'Yes' : 'No'),
                })),
              ],
              rows: benefits,
            })
          }
          disabled={loading || benefits.length === 0}
        >
          Export to CSV
        </Button>
      </div>

      {loadError && (
        <div className={styles.errorBar}>
          <MessageBar intent="error">
            <MessageBarBody>{loadError}</MessageBarBody>
            <MessageBarActions>
              <Button appearance="transparent" onClick={() => void refresh()}>
                Retry
              </Button>
            </MessageBarActions>
          </MessageBar>
        </div>
      )}
      {actionError && (
        <div className={styles.errorBar}>
          <MessageBar intent="error">
            <MessageBarBody>{actionError}</MessageBarBody>
            <MessageBarActions>
              <Button appearance="transparent" onClick={() => setActionError(null)}>
                Dismiss
              </Button>
            </MessageBarActions>
          </MessageBar>
        </div>
      )}

      <div className={styles.benefitsCount}>
        <Text size={200}>
          {displayBenefits.length} {displayBenefits.length === 1 ? 'benefit' : 'benefits'}
        </Text>
      </div>

      <div ref={gridContainerRef} className={styles.gridContainer}>
        {hasPendingChanges ? (
          <div className={styles.pendingLegend} role="note" aria-label="Pending changes legend">
            <span><strong>Pending changes legend:</strong></span>
            {hasPendingCreate ? (
              <span className={styles.pendingLegendItem}>
                <span className={styles.categoryNameSwatchCreate} aria-hidden>Aa</span>
                New benefit
              </span>
            ) : null}
            {hasPendingRename ? (
              <span className={styles.pendingLegendItem}>
                <span className={styles.categoryNameSwatchRename} aria-hidden>Aa</span>
                Renamed benefit
              </span>
            ) : null}
            {hasPendingDelete ? (
              <span className={styles.pendingLegendItem}>
                <span className={styles.categoryNameSwatchDelete} aria-hidden>Aa</span>
                Benefit to be removed
              </span>
            ) : null}
            {hasEligibilityAdded ? (
              <span className={styles.pendingLegendItem}>
                <span
                  className={mergeClasses(styles.pendingLegendSwatch, styles.tierEligibilityAddedSwatch)}
                  aria-hidden
                />
                Tier marked eligible
              </span>
            ) : null}
            {hasEligibilityRemoved ? (
              <span className={styles.pendingLegendItem}>
                <span
                  className={mergeClasses(styles.pendingLegendSwatch, styles.tierEligibilityRemovedSwatch)}
                  aria-hidden
                />
                Tier marked not eligible
              </span>
            ) : null}
          </div>
        ) : null}
        {showSkeleton ? (
          <Skeleton aria-label="Loading benefits">
            {Array.from({ length: 4 }).map((_, idx) => (
              <div
                key={idx}
                className={mergeClasses(styles.skeletonRow, styles.skeletonBlock)}
              >
                <SkeletonItem />
              </div>
            ))}
          </Skeleton>
        ) : (
          <DataGrid
            key={`grid-${Math.round(containerWidth / 40)}-${isLocked ? 'l' : 'u'}`}
            items={displayBenefits}
            columns={columns}
            sortable
            resizableColumns
            resizableColumnsOptions={{ autoFitColumns: false }}
            columnSizingOptions={columnSizingOptions}
            getRowId={(item) => (item as Benefit).id}
            size="medium"
          >
            <DataGridHeader>
              <DataGridRow>
                {({ renderHeaderCell }) => (
                  <DataGridHeaderCell focusMode="none">
                    {renderHeaderCell()}
                  </DataGridHeaderCell>
                )}
              </DataGridRow>
            </DataGridHeader>
            <DataGridBody<Benefit>>
              {({ item, rowId }) => (
                <DataGridRow<Benefit>
                  key={rowId}
                  className={editingId === item.id ? styles.benefitRowEditing : undefined}
                >
                  {({ columnId }) => (
                    <DataGridCell>
                      <TableCellLayout>
                        {renderCellContent(columnId as ColumnId, item)}
                      </TableCellLayout>
                    </DataGridCell>
                  )}
                </DataGridRow>
              )}
            </DataGridBody>
          </DataGrid>
        )}
      </div>

      {/* Delete confirmation dialog */}
      <Dialog
        open={pendingDeleteId !== null}
        onOpenChange={(_, data) => {
          if (!data.open && deletingId === null) {
            setPendingDeleteId(null);
            setDeleteError(null);
          }
        }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Delete benefit?</DialogTitle>
            <DialogContent>
              {(() => {
                const pendingOp = pendingBenefit?.pending?.operation;
                if (pendingOp === 'create') {
                  return (
                    <Text>
                      Discard the pending creation of{' '}
                      <strong>{pendingBenefit?.name ?? 'this benefit'}</strong>?
                      It has not been applied yet, so this just removes the
                      pending change.
                    </Text>
                  );
                }
                const hasPendingEdit = pendingOp === 'update' || pendingOp === 'rename';
                return (
                  <>
                    <Text>
                      Are you sure you want to delete{' '}
                      <strong>{pendingBenefit?.name ?? 'this benefit'}</strong>?
                    </Text>
                    {hasPendingEdit && (
                      <MessageBar intent="warning" className={styles.deleteDialogWarning}>
                        <MessageBarBody>
                          This benefit has pending changes that have not been
                          applied yet. Deleting it will discard those pending
                          changes.
                        </MessageBarBody>
                      </MessageBar>
                    )}
                  </>
                );
              })()}
              {deleteError && (
                <MessageBar intent="error" className={styles.deleteDialogWarning}>
                  <MessageBarBody>{deleteError}</MessageBarBody>
                  <MessageBarActions>
                    <Button
                      appearance="transparent"
                      onClick={() => setDeleteError(null)}
                    >
                      Dismiss
                    </Button>
                  </MessageBarActions>
                </MessageBar>
              )}
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="secondary" disabled={deletingId !== null}>
                  Cancel
                </Button>
              </DialogTrigger>
              <Button
                appearance="primary"
                onClick={() => void confirmDelete()}
                disabled={deletingId !== null}
              >
                {deletingId !== null ? 'Deleting…' : 'Delete'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};
